import React, { useReducer } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { getItemFromPageModel, getProcessEnvs, prePageCall, respGridUtil } from '@marriott/mi-shop-components/utils';
import { getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { HQVContext, hqvInitialState, hqvReducer, Layout, ErrorHandlerAlert } from '@marriott/mi-shop-components';
import { constants } from '@marriott/mi-shop-components/constants';
import { useNextMiApolloClientWithoutCache, isLocalDev } from '@marriott/mi-apollo-client-utils';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH } = getProcessEnvs();
const { ITEM_PATH_ROOT } = constants;

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function FindHotelPage({ model, deployedEnvType }) {
  const MiApolloClient = useNextMiApolloClientWithoutCache(operationSignatures, deployedEnvType);
  const responsiveGridModel = getItemFromPageModel(model, ITEM_PATH_ROOT);
  const responsiveGridModelProps = respGridUtil(responsiveGridModel);
  const { metaNames, metaProperties, title, staticComponentClientLibsJS, staticComponentClientLibsCSS } = model;
  const { styleclass } = responsiveGridModel;

  const layoutClientlibCssPath = isLocalDev ? '../../clientlib-static-component.min.css' : staticComponentClientLibsCSS;
  const layoutClientLibJsPath = isLocalDev ? '../../clientlib-static-component.min.js' : staticComponentClientLibsJS;

  const [state, dispatch] = useReducer(hqvReducer, hqvInitialState);

  return (
    <ErrorHandlerAlert alertMessage={model?.['componentErrorMessage']}>
      <ApolloProvider client={MiApolloClient}>
        <HQVContext.Provider value={{ hqvState: state, hqvDispatch: dispatch }}>
          <Layout
            metaNames={metaNames}
            metaProps={metaProperties}
            title={title}
            headerFooterJS={layoutClientLibJsPath}
            headerFooterCSS={layoutClientlibCssPath}
            pageIdentifier={styleclass}
            model={model}
          >
            <ResponsiveGrid {...responsiveGridModelProps} pagePath={NEXT_PUBLIC_AEM_PATH} itemPath={ITEM_PATH_ROOT} />
          </Layout>
        </HQVContext.Provider>
      </ApolloProvider>
    </ErrorHandlerAlert>
  );
}

export const getServerSideProps = prePageCall(() => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();
  // passing session get and post url to get this in client side calls
  const {
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    MI_DATALAYER_APP_URL,
    NEXT_PUBLIC_DATALAYER_SITENAME,
    NEXT_PUBLIC_DATALAYER_PRODUCT,
    NEXT_PUBLIC_DATALAYER_APPLICATION,
    NEXT_PUBLIC_DATALAYER_TEMPLATE,
    NEXT_PUBLIC_DATALAYER_VARIATION,
    NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
    NEXT_PUBLIC_ORIENTATION_PORTRAIT,
    NEXT_PUBLIC_PATHTYPE_NEW,
    NEXT_PUBLIC_PATHTYPE_LEGACY,
    NEXT_PUBLIC_SEARCH_SINGLE_RATE,
    NEXT_PUBLIC_RAM_TILE_NAME,
    NEXT_PUBLIC_PAGE_BREAKPT,
    NEXT_PUBLIC_SEARCH_MAPVIEW,
    NEXT_PUBLIC_SEARCH_LISTVIEW,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    REDIRECT_URL,
    GOOGLE_MAP_API_KEY,
    NEXT_PUBLIC_PREFIX,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    ENABLE_TARGET_SEGMENT_CALL,
  } = getProcessEnvs();
  return {
    props: {
      deployedEnvType,
      SESSION_APP_GET_CALL_URL,
      SESSION_APP_POST_CALL_URL,
      NEXT_PUBLIC_PRE_PROCESSING_URL,
      NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
      datalayerParams: {
        MI_DATALAYER_APP_URL,
        NEXT_PUBLIC_DATALAYER_SITENAME,
        NEXT_PUBLIC_DATALAYER_PRODUCT,
        NEXT_PUBLIC_DATALAYER_APPLICATION,
        NEXT_PUBLIC_DATALAYER_TEMPLATE,
        NEXT_PUBLIC_DATALAYER_VARIATION,
      },
      datalayerProperties: {
        NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
        NEXT_PUBLIC_ORIENTATION_PORTRAIT,
        NEXT_PUBLIC_PATHTYPE_NEW,
        NEXT_PUBLIC_PATHTYPE_LEGACY,
        NEXT_PUBLIC_SEARCH_SINGLE_RATE,
        NEXT_PUBLIC_RAM_TILE_NAME,
        NEXT_PUBLIC_PAGE_BREAKPT,
        NEXT_PUBLIC_SEARCH_MAPVIEW,
        NEXT_PUBLIC_SEARCH_LISTVIEW,
      },
      REDIRECT_URL,
      GOOGLE_MAP_API_KEY,
      NEXT_PUBLIC_PREFIX,
      CACHE_CLEAR_URL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      ENABLE_TARGET_SEGMENT_CALL,
    },
  };
  /* Read NEXT_PUBLIC_PRE_PROCESSING_URL from process.env and pass, and
  pass it in pageProps for client-side calls */
});
